/**
 * Product Detail — Hybrid Elite layout.
 *
 * Sections:
 *   1. Identity (description, UoM, HS Code, Tax rule, Compliance)
 *   2. Suppliers (master counterparty)
 *   3. Clients (master counterparty)
 *   4. Prices (historical, with evolution)
 *   5. Integrations (POs, Invoices, Deals)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Package, Tag, Shield, Truck, Users, DollarSign,
  ShoppingCart, FileText, Handshake, Plus, Trash2, Pencil, Save, X,
  Loader2, Clock, Hash,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/use-toast';
import MasterCounterpartyPicker from '../../../components/admin/MasterCounterpartyPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [pos, setPos] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [deals, setDeals] = useState([]);
  const [showAddPrice, setShowAddPrice] = useState(false);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);

  const token = useMemo(() => localStorage.getItem('admin_token'), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const h = { headers: { Authorization: `Bearer ${token}` } };
      const [p, po, inv, dl] = await Promise.all([
        axios.get(`${API_URL}/api/products/${id}`, h),
        axios.get(`${API_URL}/api/products/${id}/purchase-orders`, h).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/products/${id}/invoices`, h).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/products/${id}/deals`, h).catch(() => ({ data: [] })),
      ]);
      setProduct(p.data);
      setForm(p.data);
      setPos(po.data || []);
      setInvoices(inv.data || []);
      setDeals(dl.data || []);
    } catch (err) {
      toast({ title: 'Failed to load product', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [id, token, toast]);

  useEffect(() => { load(); }, [load]);

  const saveEdit = async () => {
    try {
      const t = localStorage.getItem('admin_token');
      const payload = { ...form };
      ['id', 'created_at', 'updated_at', 'created_by', 'updated_by', 'audit_log', 'historical_prices'].forEach((k) => delete payload[k]);
      const r = await axios.patch(`${API_URL}/api/products/${id}`, payload, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setProduct(r.data);
      setEdit(false);
      toast({ title: 'Saved' });
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const addSupplier = async (counterpartyId) => {
    try {
      const t = localStorage.getItem('admin_token');
      const r = await axios.post(`${API_URL}/api/products/${id}/suppliers`, { counterparty_id: counterpartyId }, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setProduct(r.data);
      setShowAddSupplier(false);
      toast({ title: 'Supplier added' });
    } catch (err) {
      toast({ title: 'Failed', variant: 'destructive' });
    }
  };

  const removeSupplier = async (cpId) => {
    if (!window.confirm('Remove this supplier from the product?')) return;
    try {
      const t = localStorage.getItem('admin_token');
      const r = await axios.delete(`${API_URL}/api/products/${id}/suppliers/${cpId}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setProduct(r.data);
    } catch (_) {/* silent */}
  };

  const addClient = async (counterpartyId) => {
    try {
      const t = localStorage.getItem('admin_token');
      const r = await axios.post(`${API_URL}/api/products/${id}/clients`, { counterparty_id: counterpartyId }, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setProduct(r.data);
      setShowAddClient(false);
      toast({ title: 'Client added' });
    } catch (err) {
      toast({ title: 'Failed', variant: 'destructive' });
    }
  };

  const removeClient = async (cpId) => {
    if (!window.confirm('Remove this client from the product?')) return;
    try {
      const t = localStorage.getItem('admin_token');
      const r = await axios.delete(`${API_URL}/api/products/${id}/clients/${cpId}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setProduct(r.data);
    } catch (_) {/* silent */}
  };

  if (loading) return <div className="p-8 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!product) return <div className="p-8 text-center text-red-600">Product not found.</div>;

  const prices = [...(product.historical_prices || [])].sort((a, b) => (b.effective_date || '').localeCompare(a.effective_date || ''));

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="product-detail-page">
      <button onClick={() => navigate('/admin/products')}
              className="text-sm text-[#023E8A] hover:underline mb-4 inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to products
      </button>

      {/* HEADER */}
      <Card className="mb-5 overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-[#023E8A] to-[#0353A4]" />
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#023E8A] to-[#0353A4] flex items-center justify-center">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-sm text-[#023E8A] bg-[#023E8A]/5 px-2 py-0.5 rounded font-bold" data-testid="product-sku">{product.sku}</code>
                    {!product.is_active && <Badge className="bg-slate-400 text-white">inactive</Badge>}
                  </div>
                  <h1 className="text-2xl font-bold text-[#2B2D42]">{product.name}</h1>
                </div>
              </div>
              {(product.compliance_flags || []).length > 0 && (
                <div className="flex gap-1 flex-wrap mt-2">
                  {product.compliance_flags.map((f) => (
                    <Badge key={f} className="bg-amber-100 text-amber-800 text-xs">
                      <Shield className="w-3 h-3 mr-0.5" />{f}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {!edit && <Button variant="outline" onClick={() => setEdit(true)} data-testid="product-edit-btn"><Pencil className="w-4 h-4 mr-1" />Edit</Button>}
              {edit && <>
                <Button variant="outline" onClick={() => { setEdit(false); setForm(product); }}><X className="w-4 h-4 mr-1" />Cancel</Button>
                <Button onClick={saveEdit} className="bg-[#023E8A] text-white" data-testid="product-save-btn"><Save className="w-4 h-4 mr-1" />Save</Button>
              </>}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* LEFT — Identity + suppliers + clients */}
        <div className="lg:col-span-2 space-y-5">
          {/* Identity */}
          <Card>
            <CardContent className="p-5">
              <h3 className="text-sm font-bold text-[#6C757D] uppercase tracking-wide mb-3 flex items-center gap-2">
                <Tag className="w-4 h-4" /> Identity
              </h3>
              {!edit ? (
                <div className="space-y-3">
                  <Field label="Description" value={product.description || '—'} />
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Unit of Measure" value={product.unit_of_measure} />
                    <Field label="HS Code" value={product.hs_code || '—'} mono />
                    <Field label="Lead Time" value={product.lead_time_days ? `${product.lead_time_days} days` : '—'} />
                  </div>
                  <Field label="Tax Rule" value={product.tax_rule || '—'} />
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-[#6C757D] uppercase">Description</label>
                    <textarea rows={2} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                              value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <LabelInput label="UoM" value={form.unit_of_measure} onChange={(v) => setForm({ ...form, unit_of_measure: v })} />
                    <LabelInput label="HS Code" value={form.hs_code || ''} mono onChange={(v) => setForm({ ...form, hs_code: v })} />
                    <LabelInput label="Lead (days)" type="number" value={form.lead_time_days || ''} onChange={(v) => setForm({ ...form, lead_time_days: v ? parseInt(v, 10) : null })} />
                  </div>
                  <LabelInput label="Tax Rule" value={form.tax_rule || ''} onChange={(v) => setForm({ ...form, tax_rule: v })} />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Suppliers */}
          <CounterpartySection
            title="Default Suppliers"
            icon={Truck}
            ids={product.default_suppliers || []}
            onAdd={() => setShowAddSupplier(true)}
            onRemove={removeSupplier}
            addOpen={showAddSupplier}
            onCloseAdd={() => setShowAddSupplier(false)}
            onPick={addSupplier}
            dataTestid="product-suppliers"
          />

          {/* Clients */}
          <CounterpartySection
            title="Default Clients"
            icon={Users}
            ids={product.default_clients || []}
            onAdd={() => setShowAddClient(true)}
            onRemove={removeClient}
            addOpen={showAddClient}
            onCloseAdd={() => setShowAddClient(false)}
            onPick={addClient}
            dataTestid="product-clients"
          />
        </div>

        {/* RIGHT — prices + integrations */}
        <div className="space-y-5">
          {/* Prices */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-[#6C757D] uppercase tracking-wide flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Price History
                </h3>
                <Button size="sm" variant="outline" onClick={() => setShowAddPrice(true)} data-testid="product-add-price-btn">
                  <Plus className="w-3 h-3 mr-1" />Add
                </Button>
              </div>
              {prices.length === 0 ? (
                <p className="text-xs text-slate-400">No prices recorded yet.</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {prices.map((p) => (
                    <div key={p.id} className="border rounded p-2 text-xs" data-testid={`product-price-${p.id}`}>
                      <div className="flex items-center justify-between">
                        <Badge className={`text-[10px] ${p.kind === 'cost' ? 'bg-red-100 text-red-800' : p.kind === 'list' ? 'bg-emerald-100 text-emerald-800' : 'bg-violet-100 text-violet-800'}`}>
                          {p.kind}
                        </Badge>
                        <span className="font-mono font-bold text-[#2B2D42]">{p.currency} {Number(p.amount).toLocaleString()}</span>
                      </div>
                      {p.note && <div className="text-[#6C757D] mt-1">{p.note}</div>}
                      <div className="text-[10px] text-slate-400 mt-1">
                        <Clock className="w-2.5 h-2.5 inline mr-0.5" />
                        {new Date(p.effective_date).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Integrations */}
          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="text-sm font-bold text-[#6C757D] uppercase tracking-wide flex items-center gap-2">
                <Hash className="w-4 h-4" /> Integrations
              </h3>
              <IntegrationStat icon={ShoppingCart} label="Purchase Orders" count={pos.length} />
              <IntegrationStat icon={FileText} label="Invoices" count={invoices.length} />
              <IntegrationStat icon={Handshake} label="CRM Deals" count={deals.length} />
              <p className="text-[10px] text-slate-400">Cross-module references appear as POs/invoices/deals adopt this SKU.</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {showAddPrice && (
        <AddPriceModal productId={id} onClose={() => setShowAddPrice(false)}
                       onAdded={(updated) => { setShowAddPrice(false); load(); }} />
      )}
    </div>
  );
};


const Field = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] font-bold uppercase text-[#6C757D] tracking-wide">{label}</div>
    <div className={`text-sm text-[#2B2D42] mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</div>
  </div>
);

const LabelInput = ({ label, value, onChange, type = 'text', mono }) => (
  <div>
    <label className="text-xs font-semibold text-[#6C757D] uppercase">{label}</label>
    <input type={type} className={`mt-1 w-full px-2 py-1.5 border rounded text-sm ${mono ? 'font-mono' : ''}`}
           value={value || ''} onChange={(e) => onChange(e.target.value)} />
  </div>
);

const IntegrationStat = ({ icon: Icon, label, count }) => (
  <div className="flex items-center justify-between p-2 border rounded">
    <div className="flex items-center gap-2 text-sm text-[#2B2D42]">
      <Icon className="w-4 h-4 text-[#023E8A]" /> {label}
    </div>
    <Badge className="bg-slate-100 text-[#2B2D42]">{count}</Badge>
  </div>
);

const CounterpartySection = ({ title, icon: Icon, ids, onAdd, onRemove, addOpen, onCloseAdd, onPick, dataTestid }) => {
  const [items, setItems] = useState({});
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    ids.forEach((id) => {
      if (items[id]) return;
      axios.get(`${API_URL}/api/counterparties/${id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setItems((prev) => ({ ...prev, [id]: r.data })))
        .catch(() => setItems((prev) => ({ ...prev, [id]: { id, name: id } })));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  return (
    <Card data-testid={dataTestid}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#6C757D] uppercase tracking-wide flex items-center gap-2">
            <Icon className="w-4 h-4" /> {title} ({ids.length})
          </h3>
          <Button size="sm" variant="outline" onClick={onAdd} data-testid={`${dataTestid}-add-btn`}>
            <Plus className="w-3 h-3 mr-1" />Add
          </Button>
        </div>
        {addOpen && (
          <div className="mb-3 p-2 border border-dashed border-[#023E8A]/40 rounded bg-[#023E8A]/5">
            <MasterCounterpartyPicker value={null} onSelect={(cp) => { if (cp) onPick(cp.id); }} />
            <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={onCloseAdd}>Cancel</Button>
          </div>
        )}
        {ids.length === 0 ? (
          <p className="text-xs text-slate-400">None yet.</p>
        ) : (
          <div className="space-y-1.5">
            {ids.map((cpId) => {
              const item = items[cpId];
              return (
                <div key={cpId} className="flex items-center gap-2 p-2 border rounded text-sm" data-testid={`${dataTestid}-row-${cpId}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#2B2D42] truncate">{item?.name || cpId}</div>
                    {item?.country && <div className="text-[10px] text-[#6C757D]">{item.country}</div>}
                  </div>
                  <button onClick={() => onRemove(cpId)} className="p-1 text-red-500 hover:bg-red-50 rounded"
                          data-testid={`${dataTestid}-remove-${cpId}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


const AddPriceModal = ({ productId, onClose, onAdded }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ kind: 'cost', currency: 'AED', amount: '', note: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.amount || Number.isNaN(Number(form.amount))) {
      toast({ title: 'Amount required', variant: 'destructive' }); return;
    }
    setSaving(true);
    try {
      const t = localStorage.getItem('admin_token');
      await axios.post(`${API_URL}/api/products/${productId}/prices`, {
        ...form, amount: parseFloat(form.amount),
      }, { headers: { Authorization: `Bearer ${t}` } });
      onAdded();
    } catch (err) {
      toast({ title: 'Save failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
        <h3 className="text-lg font-bold text-[#2B2D42] mb-3">Add Price</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-[#6C757D] uppercase">Kind</label>
            <select className="mt-1 w-full px-2 py-2 border rounded text-sm" value={form.kind}
                    onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              <option value="cost">Cost (supplier)</option>
              <option value="list">List (sale)</option>
              <option value="contract">Contract (negotiated)</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <LabelInput label="Currency" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} />
            <LabelInput label="Amount" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} />
          </div>
          <LabelInput label="Note" value={form.note} onChange={(v) => setForm({ ...form, note: v })} />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#023E8A] text-white" data-testid="product-price-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
